import React, { useContext, useRef, useMemo, useState } from 'react';
import classnames from 'classnames';
import { AutoComplete, SearchBox, useOutsideClick } from 'tea-component';
import { useIntl } from 'umi';
import { useLatest, useMount, useUnmount } from 'ahooks';
import { normalizeKeyword, getMatchRanges, renderHighlightedText, flattenSearchableMenus } from './utils';
import { LANGUAGE_CN } from '@home/_config/config';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { dbiBtnLogMes } from '@intelligence/utils/buriedPointCfg';
import { MenuClickContext, MenuListContext } from '../../MenuListContext';

import type { IDBIMenuVO } from '@intelligence/types/menu';

import './index.scss';

interface IMenuSearchOption {
  groupKey: string;
  value: string;
  text: React.ReactNode;
  rawText: string;
  menu: IDBIMenuVO;
}

function useCloseWhenOutsideClick(searchRef: React.MutableRefObject<HTMLDivElement | null>, handler?: () => void) {
  const { listen, remove } = useOutsideClick(searchRef);
  const handlerRef = useLatest(handler);

  useMount(() => {
    listen(() => {
      handlerRef.current?.();
    });
  });

  useUnmount(() => {
    remove();
  });
}

const MenuSearch: React.FC = () => {
  const { locale, formatMessage } = useIntl();
  const isCn = locale === LANGUAGE_CN;
  const searchRef = useRef<HTMLDivElement | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const menuList = useContext(MenuListContext);
  const clickMenu = useContext(MenuClickContext);

  useCloseWhenOutsideClick(searchRef, () => {
    setOpen(false);
  });

  const searchOptions = useMemo(() => {
    const options = flattenSearchableMenus(isCn, menuList);
    const keyword = normalizeKeyword(inputValue);

    if (!keyword) {
      return [];
    }

    const result: IMenuSearchOption[] = [];

    options.forEach((option) => {
      const matchRanges = getMatchRanges(option.rawText, keyword);

      if (matchRanges.length === 0) {
        return;
      }

      result.push({
        ...option,
        text: renderHighlightedText(option.rawText, matchRanges),
      });
    });

    return result;
  }, [isCn, inputValue, menuList]);

  const handleSelect = (value: string) => {
    const targetOption = searchOptions.find((option) => option.value === value);

    if (!targetOption) {
      return;
    }

    const searchKeyword = inputValue.trim();

    setInputValue('');
    setOpen(false);
    buriedBtnPoint({
      ...dbiBtnLogMes.menuSearchSelect,
      extInfo: `${searchKeyword} -> ${targetOption.rawText}`,
    });
    clickMenu?.(targetOption.menu);
  };

  const shouldOpen = !!inputValue && open;

  return (
    <div className="dbi-menu-search" ref={searchRef}>
      <AutoComplete
        className="dbi-menu-search__auto-complete"
        overlayClassName={classnames('dbi-menu-search__overlay', {
          'dbi-menu-search__overlay--empty': !searchOptions?.length,
        })}
        open={shouldOpen}
        options={searchOptions}
        groups={{ page: formatMessage({ id: 'dbi_menu_search_page_group' }) }}
        tips={formatMessage({ id: 'dbi_search_no_result' })}
        onChange={handleSelect}
        popupContainer={searchRef.current || undefined}
      >
        {(ref) => (
          <SearchBox
            className="dbi-menu-search__input"
            placeholder={formatMessage({ id: 'dbi_menu_search_placeholder' })}
            ref={ref}
            value={inputValue}
            onChange={(value) => {
              const nextValue = value || '';
              setInputValue(nextValue);
              setOpen(!!nextValue);
            }}
            onClick={() => {
              setOpen(!!inputValue);
            }}
            onFocus={() => {
              setOpen(!!inputValue);
            }}
            onClear={() => {
              setInputValue('');
              setOpen(false);
            }}
          />
        )}
      </AutoComplete>
    </div>
  );
};

export default MenuSearch;
